import React from 'react';

const AVATAR_COLORS = [
  '#1D4ED8', // brand blue
  '#059669', // emerald
  '#7C3AED', // violet
  '#2563EB', // blue
  '#C026D3', // fuchsia
  '#DC2626', // red
  '#D97706', // amber
  '#0891B2', // cyan
];

function getInitials(name: string): string {
  return name
    .split(' ')
    .filter(Boolean)
    .map((w) => w[0])
    .join('')
    .toUpperCase()
    .slice(0, 2) || '?';
}

function getColor(seed: string): string {
  const hash = seed.split('').reduce((acc, c) => acc + c.charCodeAt(0), 0);
  return AVATAR_COLORS[hash % AVATAR_COLORS.length];
}

const SIZE_MAP = {
  xs: 'w-4 h-4 text-[7px]',
  sm: 'w-6 h-6 text-[10px]',
  md: 'w-7 h-7 text-xs',
  lg: 'w-10 h-10 text-sm',
} as const;

type AvatarSize = keyof typeof SIZE_MAP;

interface AvatarProps {
  name: string;
  src?: string | null;
  size?: AvatarSize;
  /** Use active theme brand color instead of hash-based palette */
  variant?: 'default' | 'brand';
  className?: string;
}

export default function Avatar({
  name,
  src,
  size = 'md',
  variant = 'default',
  className = '',
}: AvatarProps) {
  const sizeClass = SIZE_MAP[size];

  if (src) {
    return (
      <img
        src={src}
        alt={name}
        className={`${sizeClass} rounded-full object-cover shrink-0 ${className}`}
      />
    );
  }

  const brandClass =
    variant === 'brand'
      ? 'bg-brand text-white'
      : '';

  return (
    <span
      className={`${sizeClass} rounded-full inline-flex items-center justify-center font-semibold shrink-0 ${
        variant === 'brand' ? brandClass : 'text-white'
      } ${className}`}
      style={variant === 'default' ? { backgroundColor: getColor(name) } : undefined}
      title={name}
    >
      {getInitials(name)}
    </span>
  );
}
